import { nanoid } from 'nanoid';
import type {
  Cell,
  EditorBlock,
  Node,
  Row,
  Section,
  CustomMergeTagPayload,
  Table,
  Text,
  Field,
  NodeUpdatePayload,
  MergeValues,
  ImportContentType,
  Form,
  Signature,
  RowPayload,
  CellPayload,
  UpdateNodeRequest,
  PlaceholderElementType,
  Context,
  ContractLink,
  PricingSummaryTable,
  EmbedSlideShowType,
  LinkedResource,
} from '@getaccept/editor-lib';
import {
  NodeType,
  fallbackTableContent,
  FieldType,
  FieldInputType,
  EditorHelper as EditorShared,
  RowType,
  EditorHelper as SharedEditorHelper,
  PlaceholderType,
  SignatureHelper,
} from '@getaccept/editor-lib';
import { CurrencyHelper } from '@getaccept/lib-shared/src/helpers/currency.helper';
import { toRawDeep } from '@getaccept/lib-shared/src/utils';
import { MoveHelper } from '../../helpers/move.helper';
import type { CreateRow, SplitTextCreateRow } from '../../types';
import { EditorHelper } from '../../helpers/editor.helper';
import type {
  CreateRowRequest,
  DeleteRowRequest,
  MoveRowRequest,
  UpdateCellsRequest,
  MoveCellRequest,
  UpdateRowRequest,
  CreateSectionRequest,
  UpdateSectionRequest,
  UpdateContentRequest,
  MoveSectionRequest,
} from '../../api/services/types/requests';

export class ContentMutationHelper {
  static readonly mergeNodeValues = (node: Node, update: NodeUpdatePayload): Node => ({
    ...node,
    ...update,
    ...(node.type === NodeType.Table && {
      tableStyle: {
        ...node.tableStyle,
        ...update.tableStyle,
      },
    }),
    ...(node.type === NodeType.Image && {
      imageTransforms:
        update.imageTransforms === null
          ? update.imageTransforms
          : {
              ...node.imageTransforms,
              ...update.imageTransforms,
            },
    }),
  });

  static findIndexById(array: { id: string }[], targetId: string, append: boolean) {
    return array.findIndex(({ id }) => id === targetId) + (append ? 1 : 0);
  }

  static insertIntoArray(array: { id: string }[], targetId, append, newItem) {
    const index = ContentMutationHelper.findIndexById(array, targetId, append);
    return [...array.slice(0, index), newItem, ...array.slice(index)];
  }

  static getNewRow({
    rowId: id,
    rowType: type,
    cells,
    columnCount,
    _tempSlideShowId,
    _tempSlideShowName,
  }: RowPayload): Row {
    return {
      id,
      type,
      columnCount,
      _tempSlideShowId,
      _tempSlideShowName,
      cells: cells.map(ContentMutationHelper.getNewCell),
    };
  }

  private static getNewCell({ width, cellId: id, cellType: type, nodes }: CellPayload) {
    return {
      width,
      id,
      type,
      nodes: nodes.map(EditorShared.getNewNode),
    };
  }

  static getNewInputField(field: Partial<Field>, isDsr = false): Field {
    return {
      ...field,
      _tempSelectionId: nanoid(EditorShared.NANO_ID_LENGTH),
      id: nanoid(EditorShared.NANO_ID_LENGTH),
      type: FieldType.Input,
      inputSettings: {
        ...field.inputSettings,
        type: field?.inputSettings?.type || FieldInputType.Text,
        width: 100,
        required: isDsr ? false : true,
      },
    };
  }

  static cascadeUpdateCustomMergeTags(
    blocks: EditorBlock[],
    payload: CustomMergeTagPayload
  ): EditorBlock[] {
    const updateFields = (
      node: Table | Text,
      { customName, value }: CustomMergeTagPayload
    ): Node => ({
      ...node,
      fields: node.fields.map(field =>
        field.customName === customName ? { ...field, value } : field
      ),
    });

    return blocks.map((block: EditorBlock) => ({
      ...block,
      content: {
        ...block.content,
        sections: block.content.sections.map((section: Section) => ({
          ...section,
          rows: section.rows.map((row: Row) => ({
            ...row,
            cells: row.cells.map((cell: Cell) => ({
              ...cell,
              nodes: cell.nodes.map((node: Text | Table) =>
                node.fields ? updateFields(node, payload) : node
              ),
            })),
          })),
        })),
      },
    }));
  }

  static getCreatePayloadByType({
    createPayload,
    type,
    formInputType,
    mergeValues,
    subType,
    context,
    placeholderType,
    placeholderElementType,
    pricingSummaryTables,
    embedSlideShowType,
  }: {
    createPayload: Partial<CreateRow> | Partial<SplitTextCreateRow>;
    type: NodeType | RowType;
    formInputType: FieldInputType;
    mergeValues: MergeValues;
    subType?: ImportContentType;
    context?: Context;
    placeholderType?: PlaceholderType;
    placeholderElementType?: PlaceholderElementType;
    pricingSummaryTables?: PricingSummaryTable[];
    embedSlideShowType?: EmbedSlideShowType;
  }) {
    switch (type) {
      case RowType.Columns:
      case RowType.SideBySide:
        return { ...createPayload, rowType: type };
      case RowType.SlideShow:
        return { ...createPayload, rowType: type, subType };

      case NodeType.Table:
        return {
          ...createPayload,
          nodePayload: { nodeType: type, content: fallbackTableContent },
        };

      case NodeType.Form: {
        const field = ContentMutationHelper.getNewInputField(
          {
            ...EditorHelper.getAssignIds(mergeValues, context),
            inputSettings: { type: formInputType },
          },
          EditorShared.isDealroomContext(context)
        );
        return {
          ...createPayload,
          nodePayload: {
            heading: '',
            nodeType: type,
            inputFieldSets: [{ ids: [field.id] }],
            fields: [field],
          },
        };
      }
      case NodeType.Signature: {
        const fields = [];
        const recipients = SignatureHelper.getRecipientSigners(mergeValues.recipients);

        recipients.forEach(recipient => {
          fields.push(
            ContentMutationHelper.getNewInputField(
              EditorHelper.getAssignIdsForSignature({ recipientId: recipient.id })
            )
          );
        });
        fields.push(
          ContentMutationHelper.getNewInputField(
            EditorHelper.getAssignIdsForSignature({ userId: mergeValues.sender.id })
          )
        );
        return {
          ...createPayload,
          nodePayload: {
            nodeType: type,
            fields,
          },
        };
      }
      case NodeType.Text:
      case NodeType.Image:
      case NodeType.Divider:
      case NodeType.Video:
      case NodeType.Link:
      case NodeType.ContractLink:
        return {
          ...createPayload,
          nodePayload: { nodeType: type },
        };

      case NodeType.EmbedSlideShow:
        return {
          ...createPayload,
          nodePayload: { nodeType: type, embedSlideShowType },
        };

      case NodeType.ContentPlaceholder:
        if (placeholderType === PlaceholderType.Resource) {
          return {
            ...createPayload,
            nodePayload: {
              nodeType: type,
              placeholder: {
                type: PlaceholderType.Resource,
                resourceIds: [],
              },
            },
          };
        }

        return {
          ...createPayload,
          nodePayload: {
            nodeType: type,
            placeholder: {
              type: PlaceholderType.Element,
              elementType: placeholderElementType,
            },
          },
        };

      case NodeType.PricingTable:
        return {
          ...createPayload,
          nodePayload: {
            nodeType: type,
            currencySettings: CurrencyHelper.getDefaultCurrencySettings(mergeValues?.entity),
          },
        };

      case NodeType.PricingSummary:
        return {
          ...createPayload,
          nodePayload: {
            nodeType: type,
            tables: pricingSummaryTables,
          },
        };

      default:
        return createPayload;
    }
  }

  static replaceBlock = (blocks: EditorBlock[], newBlock: EditorBlock) =>
    blocks.map((block: EditorBlock) => (block.contentId === newBlock.contentId ? newBlock : block));

  static updateNodeInSections = (
    sections: Section[],
    {
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload,
    }: Partial<UpdateNodeRequest>
  ) =>
    sections.map(section =>
      section.id !== targetSectionId
        ? section
        : {
            ...section,
            rows: section.rows.map(row =>
              row.id !== targetRowId
                ? row
                : {
                    ...row,
                    cells: row.cells.map(cell =>
                      cell.id !== targetCellId
                        ? cell
                        : {
                            ...cell,
                            nodes: cell.nodes.map(node =>
                              node.id !== targetNodeId
                                ? node
                                : ContentMutationHelper.mergeNodeValues(node, payload)
                            ),
                          }
                    ),
                  }
            ),
          }
    );

  static updateNode = (
    blocks: EditorBlock[],
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload,
    }: Partial<UpdateNodeRequest>
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: ContentMutationHelper.updateNodeInSections(block.content.sections, {
                targetSectionId,
                targetRowId,
                targetCellId,
                targetNodeId,
                payload,
              }),
            },
          }
    );

  static updateCells = (
    blocks: EditorBlock[],
    { contentId, targetSectionId, targetRowId, payload }: UpdateCellsRequest
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.map(section =>
                section.id !== targetSectionId
                  ? section
                  : {
                      ...section,
                      rows: section.rows.map(row =>
                        row.id !== targetRowId
                          ? row
                          : {
                              ...row,
                              cells: row.cells.map(cell => {
                                const cellToUpdate = payload.find(
                                  ({ cellId }) => cellId === cell.id
                                );
                                return cellToUpdate
                                  ? {
                                      ...cell,
                                      width: cellToUpdate.width,
                                      type: cellToUpdate.cellType,
                                    }
                                  : cell;
                              }),
                            }
                      ),
                    }
              ),
            },
          }
    );

  static moveCell = (
    blocks: EditorBlock[],
    {
      contentId,
      targetCellId,
      sourceSectionId,
      sourceRowId,
      sourceCellId,
      append: appendCell = true,
    }: MoveCellRequest
  ) => {
    const cell: Cell = blocks
      .find(block => block.contentId === contentId)
      .content.sections.find(({ id }) => id === sourceSectionId)
      .rows.find(({ id }) => id === sourceRowId)
      .cells.find(({ id }) => id === sourceCellId);

    return blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.map(section =>
                section.id !== sourceSectionId
                  ? section
                  : {
                      ...section,
                      rows: section.rows.map(row =>
                        row.id !== sourceRowId
                          ? row
                          : {
                              ...row,
                              cells: ContentMutationHelper.insertIntoArray(
                                row.cells.filter(({ id }) => id !== sourceCellId),
                                targetCellId,
                                appendCell,
                                cell
                              ),
                            }
                      ),
                    }
              ),
            },
          }
    );
  };

  static moveRow = (
    blocks: EditorBlock[],
    {
      contentId,
      targetContentId,
      targetSectionId,
      targetRowId,
      sourceSectionId,
      sourceRowId,
      append,
    }: MoveRowRequest
  ) => {
    const row = blocks
      .find(block => block.contentId === contentId)
      .content.sections.find(({ id }) => id === sourceSectionId)
      .rows.find(({ id }) => id === sourceRowId);

    if (targetContentId) {
      return this.moveRowToNewBlock(blocks, row, {
        contentId,
        sourceSectionId,
        targetContentId,
        targetSectionId,
        targetRowId,
        append,
      });
    }

    return blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.map(section =>
                section.id !== sourceSectionId
                  ? section
                  : {
                      ...section,
                      rows: ContentMutationHelper.insertIntoArray(
                        section.rows.filter(({ id }) => id !== sourceRowId),
                        targetRowId,
                        append,
                        row
                      ),
                    }
              ),
            },
          }
    );
  };

  private static insertRowIntoBlock = (
    block,
    row,
    { targetSectionId, targetRowId, append }: Partial<MoveRowRequest>
  ) => ({
    ...block,
    content: {
      ...block.content,
      sections: block.content.sections.map(section =>
        section.id === targetSectionId
          ? {
              ...section,
              rows: ContentMutationHelper.insertIntoArray(section.rows, targetRowId, append, row),
            }
          : section
      ),
    },
  });

  private static removeRowFromBlock(
    block: EditorBlock,
    sectionId: string,
    rowId: string
  ): EditorBlock {
    return {
      ...block,
      content: {
        ...block.content,
        sections: block.content.sections.map(section =>
          section.id === sectionId
            ? {
                ...section,
                rows: section.rows.filter(({ id }) => id !== rowId),
              }
            : section
        ),
      },
    };
  }

  private static moveRowToNewBlock = (
    blocks: EditorBlock[],
    row: Row,
    {
      contentId,
      sourceSectionId,
      targetContentId,
      targetSectionId,
      targetRowId,
      append,
    }: Partial<MoveRowRequest>
  ): EditorBlock[] => {
    const filteredBlocks = blocks.map((block: EditorBlock) => {
      if (block.contentId === contentId) {
        return this.removeRowFromBlock(block, sourceSectionId, row.id);
      }
      if (block.contentId === targetContentId) {
        return this.insertRowIntoBlock(block, row, { targetSectionId, targetRowId, append });
      }
      return block;
    });

    if (
      MoveHelper.isOnlyRowInBlock(
        blocks.find(block => block.contentId === contentId),
        row.id
      )
    ) {
      return filteredBlocks.filter(block => block.contentId !== contentId);
    }
    return filteredBlocks;
  };

  static blockHasLinkedResources = (blockId: string, linkedResources: LinkedResource[]) =>
    linkedResources.some(({ editorBlockId }) => editorBlockId === blockId);

  static moveSection = (
    blocks: EditorBlock[],
    { contentId, targetContentId, targetSectionId, sourceSectionId, append }: MoveSectionRequest,
    linkedResources?: LinkedResource[]
  ) => {
    const sourceSection = blocks
      .find(block => block.contentId === contentId)
      .content.sections.find(({ id }) => id === sourceSectionId);

    const blocksWithoutMovedSection = blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.filter(section => section.id !== sourceSectionId),
            },
          }
    );

    return blocksWithoutMovedSection
      .map(block =>
        block.contentId !== (targetContentId || contentId)
          ? block
          : {
              ...block,
              content: {
                ...block.content,
                sections: ContentMutationHelper.insertIntoArray(
                  block.content.sections,
                  targetSectionId,
                  append,
                  sourceSection
                ),
              },
            }
      )
      .filter(
        block =>
          block.content.sections.length ||
          (linkedResources &&
            ContentMutationHelper.blockHasLinkedResources(block.id, linkedResources))
      );
  };

  static updateRow = (
    blocks: EditorBlock[],
    { contentId, targetRowId, targetSectionId, payload: { cells, columnCount } }: UpdateRowRequest
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.map(section =>
                section.id !== targetSectionId
                  ? section
                  : {
                      ...section,
                      rows: section.rows.map(row =>
                        row.id !== targetRowId
                          ? row
                          : {
                              ...row,
                              columnCount,
                              cells: cells.map(({ cellId, cellType, width, nodes }) => ({
                                id: cellId,
                                type: cellType,
                                width,
                                nodes: nodes.map(
                                  ({ nodeType: type, nodeId: id, ...rest }) =>
                                    ({ ...rest, id, type }) as Node
                                ),
                              })),
                            }
                      ),
                    }
              ),
            },
          }
    );

  static updateSection = (
    blocks: EditorBlock[],
    { contentId, targetSectionId, payload }: UpdateSectionRequest
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.map(section =>
                section.id !== targetSectionId ? section : { ...section, ...payload }
              ),
            },
          }
    );

  static getContractLinkElements = (blocks: EditorBlock[]) =>
    blocks.flatMap(block =>
      block.content.sections.flatMap(section =>
        section.rows.flatMap(row =>
          row.cells.flatMap(cell => cell.nodes.filter(node => node.type === NodeType.ContractLink))
        )
      )
    );

  static hasUnMappedRecipients = (node: ContractLink): boolean =>
    node.recipientMaps?.some(recipientMap => !recipientMap.participantId);

  static formatNodeByType = (node: Node) => {
    const nodeCopy = structuredClone(toRawDeep(node));

    if (node.type !== NodeType.ContentPlaceholder && 'placeholder' in nodeCopy) {
      delete nodeCopy.placeholder;
    }

    switch (node.type) {
      case NodeType.Form:
      case NodeType.Signature:
        return {
          ...nodeCopy,
          fields: ((node as Form) || (node as Signature)).fields.map(field => ({
            ...field,
            _tempSelectionId: nanoid(SharedEditorHelper.NANO_ID_LENGTH),
          })),
        };
      case NodeType.ContentPlaceholder:
        return node;
      default:
        return nodeCopy;
    }
  };

  static setBlock = (blocks: EditorBlock[], block: EditorBlock) => [
    ...blocks,
    {
      ...block,
      content: {
        ...block.content,
        sections: block.content.sections.map(section => ({
          ...section,
          rows: section.rows.map(row => ({
            ...row,
            cells: row.cells.map(cell => ({
              ...cell,
              nodes: cell.nodes.map(node => ContentMutationHelper.formatNodeByType(node)),
            })),
          })),
        })),
      },
    },
  ];

  static updateContent = (blocks: EditorBlock[], payload: Partial<UpdateContentRequest>) =>
    blocks.map(block =>
      payload.targetContentIds.includes(block.contentId)
        ? { ...block, content: { ...block.content, ...payload.content } }
        : block
    );

  static removeBlock = (blocks: EditorBlock[], id: string) =>
    blocks.filter(block => block.id !== id);

  static createRow = (
    blocks: EditorBlock[],
    { contentId, targetSectionId, targetRowId, append, payload }: Partial<CreateRowRequest>
  ) => {
    const row = ContentMutationHelper.getNewRow(payload);
    return blocks.map(block =>
      block.contentId !== contentId
        ? block
        : this.insertRowIntoBlock(block, row, {
            targetSectionId,
            targetRowId,
            append,
          })
    );
  };

  static deleteRow = (
    blocks: EditorBlock[],
    { contentId, targetSectionId, targetRowId }: DeleteRowRequest
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : this.removeRowFromBlock(block, targetSectionId, targetRowId)
    );

  static deleteSection = (
    blocks: EditorBlock[],
    { contentId, targetSectionId }: DeleteRowRequest
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: block.content.sections.filter(section => section.id !== targetSectionId),
            },
          }
    );

  static createSection = (
    blocks: EditorBlock[],
    { contentId, targetSectionId, append, payload }: CreateSectionRequest
  ) =>
    blocks.map(block =>
      block.contentId !== contentId
        ? block
        : {
            ...block,
            content: {
              ...block.content,
              sections: ContentMutationHelper.insertIntoArray(
                block.content.sections,
                targetSectionId,
                append,
                {
                  id: payload.sectionId,
                  rows: payload.rows.map(row => ContentMutationHelper.getNewRow(row)),
                  displayName: payload.displayName,
                  hidden: payload.hidden,
                }
              ),
            },
          }
    );

  static setEditorBlockVersions = (blocks: EditorBlock[], idAndVersions: Record<string, string>) =>
    blocks.map(block => {
      const contentVersion = idAndVersions[block.id];
      if (contentVersion) {
        return { ...block, contentVersion };
      }
      return block;
    });
}
